// PDF generation: branded HTML → real PDF Blob via html2canvas → jsPDF image, with Web Share fallback.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PAYMENT_QR_DATA_URL } from "./pdf-assets";
import { LOGO_URL } from "./theme";

const BRAND = "#3E1837";
const BRAND_SOFT = "#5B2A52";
const ACCENT = "#CBA3B5";
const INK = "#1C191B";
const MUTED = "#6B6568";
const LINE = "#E8E3E6";
const SUBTLE = "#FAF9F6";
const PAY_BG = "#FFF7EE";
const PAY_BORDER = "#F1E1CB";

const fmtINR = (n) => `\u20B9 ${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

/* ---------- shared blocks ---------- */
const PAYMENT_BLOCK = `
<div class="section pay-block pb-avoid">
  <h3 class="section-h">Payment Details</h3>
  <div class="pay-row">
    <div class="pay-info">
      <div class="kv"><div class="l">Account Holder</div><div class="v">Studio Grape Private Limited</div></div>
      <div class="kv"><div class="l">Account No.</div><div class="v">50200028208327</div></div>
      <div class="kv"><div class="l">IFSC Code</div><div class="v">HDFC0001993</div></div>
      <div class="kv"><div class="l">UPI ID</div><div class="v">studiogrape@freecharge</div></div>
    </div>
    <div class="pay-qr">
      <img src="${PAYMENT_QR_DATA_URL}" alt="UPI QR" />
      <div class="qr-cap">Scan to pay via UPI</div>
    </div>
  </div>
</div>`;

const SAMPLING_TERMS = `
<div class="section">
  <h3 class="section-h">Terms &amp; Conditions</h3>
  <ol class="terms-list">
    <li>Sampling shall commence only upon approval of the quotation and receipt of 100% advance payment.</li>
    <li>Timelines are indicative and subject to receipt of all required materials, specifications, and prevailing workload.</li>
    <li>A detailed Tech Pack is mandatory. Studio Grape shall not be responsible for deviations arising from incomplete or inaccurate specifications.</li>
    <li>Pattern-making charges and sampling charges are applicable separately unless otherwise specified.</li>
    <li>By approving this quotation, the customer agrees to Studio Grape's standard Terms &amp; Conditions.</li>
  </ol>
</div>`;

const PRODUCTION_TERMS = `
<div class="section">
  <h3 class="section-h">Terms &amp; Conditions</h3>
  <ol class="terms-list">
    <li>Production timelines commence only after sample approval, order confirmation, receipt of all required materials, and agreed payment.</li>
    <li>Production prices are based on approved specifications and quantities. Any changes may result in revised pricing and timelines.</li>
    <li>Finished goods must be inspected prior to dispatch or pickup. Quality concerns must be reported within 7 days of delivery.</li>
    <li>Finished goods shall be released only upon clearance of all outstanding dues.</li>
    <li>By approving this quotation, the customer agrees to Studio Grape's standard Terms &amp; Conditions.</li>
  </ol>
</div>`;

const FULL_TERMS = `
<div class="terms-doc">
  <h2 class="terms-title">Terms &amp; Conditions</h2>
  <div class="terms-subtitle">Studio Grape · Standard commercial &amp; operational terms applicable to all engagements.</div>
  <div class="terms-section-block pb-avoid">
    <h3 class="terms-section">Sampling</h3>
    <ol class="terms-list">
      <li>The pricing, timelines, and other details mentioned in this quotation are indicative in nature and subject to revision upon receipt and review of complete project specifications, materials, references, and technical requirements.</li>
      <li>Sampling timelines are dependent on the prevailing workload at our facility. The tentative timeline for sample development shall be communicated by our team upon receipt of all required sampling materials and information.</li>
      <li>Sampling orders are processed only against 100% advance payment. Work shall commence only after the quotation has been approved and the payment has been received and confirmed by Studio Grape.</li>
      <li>A detailed Tech Pack containing all relevant specifications, measurements, construction details, trims, artwork, and finishing requirements is mandatory for sample development. In the absence of a complete Tech Pack, Studio Grape shall not be held responsible for any variation between the developed sample and the customer's intended design outcome.</li>
      <li>Pattern development is mandatory for all sampling orders. Pattern-making charges and sampling charges shall be applicable separately as mentioned in the quotation.</li>
    </ol>
  </div>
  <div class="terms-section-block pb-avoid">
    <h3 class="terms-section">Production</h3>
    <ol class="terms-list">
      <li>Standard Minimum Order Quantities (MOQs) are: Job Work — 50 pcs per design per colour; FOB — 100 pcs per design.</li>
      <li>Standard production timelines range between 30–40 days from the date of final sample approval and receipt of all required materials.</li>
      <li>Production pricing is determined based on garment construction, fabric specifications, order volumes, and associated manufacturing requirements.</li>
      <li>Production timelines shall commence only after receipt of approved samples, confirmed purchase order, advance payment (where applicable), and all required materials.</li>
      <li>All finished goods must be inspected and approved by the customer or their authorised representative at our premises prior to dispatch.</li>
      <li>Finished goods shall be dispatched only upon clearance of all outstanding dues and receipt of full payment against the invoice.</li>
      <li>A production quantity variation of up to ±5% shall be considered acceptable and shall be billed proportionately.</li>
      <li>Studio Grape shall not be responsible for delays arising from force majeure, supplier delays, logistics disruptions, governmental restrictions, labour shortages, or natural calamities.</li>
    </ol>
  </div>
  <div class="terms-section-block pb-avoid">
    <h3 class="terms-section">General</h3>
    <ol class="terms-list">
      <li>Customer meetings are conducted strictly by prior appointment only. Meeting slots are available from Monday to Friday between 12:00 PM and 5:00 PM.</li>
      <li>For all dispatches, please use our office contact number: <b>+91 81977 40003</b>.</li>
      <li>Preferred mode of communication is email. Sampling: <b>sampling@studiogrape.com</b>; Production: <b>business@studiogrape.com</b>; Business: <b>prakhar@studiogrape.com</b>.</li>
      <li>Unless otherwise specified, all quotations issued by Studio Grape shall remain valid for a period of 15 days from the date of issuance.</li>
      <li>Studio Grape reserves the right to withhold sampling, production, dispatch, or delivery of goods in the event of delayed payments or outstanding dues.</li>
    </ol>
  </div>
</div>`;

/* ---------- styles (scoped to .sg-pdf-doc) ---------- */
const STYLES = `
@page { size: A4; margin: 0; }
.sg-pdf-doc { font-family: -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif; color: ${INK}; font-size: 10.5px; line-height: 1.5; background: #fff; padding: 22px 22px 22px; box-sizing: border-box; width: 794px; }
.sg-pdf-doc * { box-sizing: border-box; font-family: inherit; color: inherit; }
.sg-pdf-doc p { margin: 0 0 8px; }
.sg-pdf-doc img { display: block; max-width: 100%; }

/* Header */
.sg-pdf-doc .head { display: flex; align-items: flex-start; justify-content: space-between; padding-bottom: 14px; margin-bottom: 20px; border-bottom: 2px solid ${INK}; }
.sg-pdf-doc .brand { display: flex; align-items: center; gap: 12px; }
.sg-pdf-doc .logo-img { width: 48px; height: 48px; border-radius: 9px; object-fit: cover; border: 1px solid ${LINE}; background: #fff; }
.sg-pdf-doc .brand h1 { font-size: 19px; font-weight: 800; margin: 0; letter-spacing: -0.3px; color: ${INK}; line-height: 1.1; }
.sg-pdf-doc .brand small { font-size: 10px; color: ${MUTED}; display: block; margin-top: 3px; letter-spacing: 0.3px; }
.sg-pdf-doc .meta { text-align: right; }
.sg-pdf-doc .meta-label { font-size: 10px; font-weight: 700; color: ${MUTED}; letter-spacing: 1px; text-transform: uppercase; }
.sg-pdf-doc .meta-qno { font-size: 17px; color: ${INK}; margin-top: 3px; font-weight: 800; letter-spacing: 0.4px; }
.sg-pdf-doc .meta-date { font-size: 10px; color: ${MUTED}; margin-top: 2px; }

/* Title */
.sg-pdf-doc h2.quote-title { font-size: 26px; font-weight: 800; letter-spacing: -0.6px; color: ${INK}; margin: 0 0 6px; line-height: 1.1; }
.sg-pdf-doc .sub { color: ${MUTED}; font-size: 11.5px; margin: 0 0 16px; }
.sg-pdf-doc .sub b { color: ${INK}; font-weight: 700; }
.sg-pdf-doc .sub-collection { color: ${INK}; font-style: italic; font-size: 11.5px; }

/* Tables — strict width so columns can't overflow */
.sg-pdf-doc table { width: 100%; max-width: 100%; border-collapse: separate; border-spacing: 0; margin: 6px 0 10px; border: 1px solid ${LINE}; border-radius: 6px; table-layout: fixed; }
.sg-pdf-doc th, .sg-pdf-doc td { padding: 8px 6px; text-align: left; font-size: 10px; vertical-align: middle; border-bottom: 1px solid ${LINE}; color: ${INK}; word-wrap: break-word; overflow-wrap: break-word; }
.sg-pdf-doc tr:last-child td { border-bottom: 0; }
.sg-pdf-doc th { font-size: 8.5px; color: ${MUTED}; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; background: ${SUBTLE}; border-bottom: 1px solid ${LINE}; line-height: 1.2; }
.sg-pdf-doc td.r, .sg-pdf-doc th.r { text-align: right; }
.sg-pdf-doc td.b, .sg-pdf-doc .b { font-weight: 700; }
.sg-pdf-doc .thumb { width: 44px; height: 44px; object-fit: cover; border-radius: 6px; border: 1px solid ${LINE}; background: ${SUBTLE}; }
.sg-pdf-doc .thumb-ph { width: 44px; height: 44px; border-radius: 6px; background: ${SUBTLE}; border: 1px solid ${LINE}; }

/* Sections — calmer, ink-colored headings */
.sg-pdf-doc .section { margin-top: 18px; }
.sg-pdf-doc .section-h { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.1px; color: ${INK}; margin: 0 0 10px; padding-bottom: 7px; border-bottom: 1px solid ${LINE}; }
.sg-pdf-doc .section-h::after { content: ""; display: inline-block; width: 22px; height: 2px; background: ${BRAND}; vertical-align: middle; margin-left: 8px; opacity: 0.8; }

/* Page-break helpers used by the JS slicer to find safe cut points */
.sg-pdf-doc .pb-anchor { display: block; height: 0; line-height: 0; font-size: 0; }
.sg-pdf-doc .pb-avoid { page-break-inside: avoid; }

/* Subtotal & Total block */
.sg-pdf-doc .subtotals-wrap { display: flex; justify-content: flex-end; margin-top: 10px; }
.sg-pdf-doc .subtotals { width: 280px; max-width: 100%; }
.sg-pdf-doc .subtotals .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 11px; }
.sg-pdf-doc .subtotals .row .l { color: ${MUTED}; }
.sg-pdf-doc .subtotals .row .v { font-weight: 700; color: ${INK}; }
.sg-pdf-doc .total { margin-top: 8px; padding: 12px 16px; border-top: 2px solid ${INK}; display: flex; justify-content: space-between; align-items: center; background: ${SUBTLE}; border-radius: 0 0 6px 6px; }
.sg-pdf-doc .total .l { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: ${INK}; font-weight: 800; }
.sg-pdf-doc .total .v { font-size: 22px; font-weight: 800; color: ${BRAND}; letter-spacing: -0.5px; line-height: 1; }

.sg-pdf-doc .styles-sub { display: flex; justify-content: flex-end; padding: 4px 0 0; font-size: 11px; color: ${INK}; font-weight: 700; }
.sg-pdf-doc .styles-sub span { color: ${MUTED}; font-weight: 600; margin-right: 8px; }

/* Key-value rows */
.sg-pdf-doc .kv-grid { display: flex; gap: 20px; flex-wrap: wrap; }
.sg-pdf-doc .kv { flex: 1; min-width: 140px; margin-bottom: 8px; }
.sg-pdf-doc .kv .l { font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.6px; color: ${MUTED}; font-weight: 700; }
.sg-pdf-doc .kv .v { font-size: 11.5px; color: ${INK}; margin-top: 3px; font-weight: 600; line-height: 1.4; }

/* Payment block */
.sg-pdf-doc .pay-block { background: ${PAY_BG}; border: 1px solid ${PAY_BORDER}; border-radius: 10px; padding: 16px; page-break-inside: avoid; }
.sg-pdf-doc .pay-block .section-h { color: ${BRAND}; border-bottom: 1px solid ${PAY_BORDER}; }
.sg-pdf-doc .pay-row { display: flex; gap: 20px; align-items: flex-start; justify-content: space-between; }
.sg-pdf-doc .pay-info { flex: 1; min-width: 200px; }
.sg-pdf-doc .pay-info .kv { margin-bottom: 8px; }
.sg-pdf-doc .pay-info .kv .v { font-size: 11.5px; }
.sg-pdf-doc .pay-qr { flex: 0 0 130px; text-align: center; }
.sg-pdf-doc .pay-qr img { width: 124px; height: 124px; border: 1px solid ${LINE}; border-radius: 8px; background: #fff; padding: 5px; }
.sg-pdf-doc .qr-cap { font-size: 9px; color: ${MUTED}; margin-top: 5px; letter-spacing: 0.3px; }

/* Terms — refined typography, elegant numbering */
.sg-pdf-doc .terms-list { font-family: Georgia, "Times New Roman", "Cambria", serif; font-size: 11px; line-height: 1.75; counter-reset: term; padding-left: 0; margin: 0; list-style: none; }
.sg-pdf-doc .terms-list > li { counter-increment: term; position: relative; padding-left: 32px; margin: 0 0 10px; text-align: justify; color: ${INK}; }
.sg-pdf-doc .terms-list > li::before { content: counter(term, decimal-leading-zero); position: absolute; left: 0; top: 0; font-family: -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 9.5px; font-weight: 700; letter-spacing: 0.6px; color: ${BRAND}; background: ${SUBTLE}; padding: 2px 7px; border-radius: 3px; line-height: 1.3; }

/* Legacy ol.terms kept for compatibility but styled like terms-list */
.sg-pdf-doc ol.terms { font-family: Georgia, "Times New Roman", serif; padding-left: 22px; margin: 0; font-size: 11px; line-height: 1.75; text-align: justify; }
.sg-pdf-doc ol.terms > li { margin: 0 0 9px; padding-left: 4px; color: ${INK}; }
.sg-pdf-doc ol.terms > li::marker { font-weight: 700; color: ${MUTED}; font-family: -apple-system, sans-serif; font-size: 10.5px; }

.sg-pdf-doc .terms-doc { padding-top: 12px; }
.sg-pdf-doc .terms-title { font-size: 22px; margin: 0 0 6px; padding-bottom: 10px; border-bottom: 2px solid ${INK}; color: ${INK}; font-weight: 800; letter-spacing: -0.4px; }
.sg-pdf-doc .terms-subtitle { font-size: 11px; color: ${MUTED}; margin: 0 0 22px; line-height: 1.55; font-family: Georgia, "Times New Roman", serif; font-style: italic; }
.sg-pdf-doc .terms-section-block { margin-top: 22px; }
.sg-pdf-doc .terms-section { font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; color: ${INK}; background: transparent; border-left: 3px solid ${BRAND}; margin: 0 0 12px; padding: 4px 12px; border-radius: 0; font-weight: 800; }

/* Footer note */
.sg-pdf-doc .foot-note { margin-top: 24px; padding-top: 12px; border-top: 1px solid ${LINE}; color: ${MUTED}; font-size: 9.5px; line-height: 1.6; }
.sg-pdf-doc .foot-cap { margin-top: 14px; text-align: center; color: ${MUTED}; font-size: 9.5px; letter-spacing: 0.4px; }
`;

/* ---------- shell ---------- */
function bodyOnlyHTML(title, body, qno, dateStr, logoSrc) {
  return `<div class="head">
  <div class="brand">
    <img class="logo-img" src="${logoSrc}" alt="" crossorigin="anonymous"/>
    <div>
      <h1>Studio Grape</h1>
      <small>Garment Manufacturing Studio</small>
    </div>
  </div>
  <div class="meta">
    <div class="meta-label">${title}</div>
    <div class="meta-qno">${qno}</div>
    <div class="meta-date">${dateStr}</div>
  </div>
</div>
${body}
<div class="foot-note">These are tentative timelines and pricing based on the current scope and market conditions. Any future revisions due to design changes, raw material fluctuations, production constraints, or operational requirements shall be communicated to the client accordingly.</div>
<div class="foot-cap">Studio Grape &middot; Garment Manufacturing Studio &middot; studiogrape.in</div>`;
}

function shell(title, body, qno, dateStr, opts = {}) {
  const printBtn = opts.forPDF ? "" : `<button class="no-print" onclick="window.print()">Print / Save PDF</button>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${title} ${qno}</title>
<style>
@page { size: A4; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
@media print { .no-print { display: none !important; } }
.no-print { position: fixed; top: 12px; right: 12px; background: ${BRAND}; color: #fff; padding: 10px 16px; border-radius: 8px; font-weight: 700; cursor: pointer; border: 0; z-index: 9999; font-size: 13px; }
${STYLES}
</style></head><body>
${printBtn}
<div class="sg-pdf-doc">
${bodyOnlyHTML(title, body, qno, dateStr, LOGO_URL)}
</div>
</body></html>`;
}

/* ---------- renderers ---------- */
function buildInitial(q) {
  const d = q.data || {};
  const samplingLine = d.sampling_price_to
    ? `${fmtINR(d.sampling_price_from)} to ${fmtINR(d.sampling_price_to)}`
    : d.sampling_price_from ? `${fmtINR(d.sampling_price_from)} onwards` : "—";
  const onwards = (v) => (v ? `${fmtINR(v)} onwards` : "—");
  const perSize = (v) => (v ? `${fmtINR(v)} per size` : "—");

  const body = `
<h2 class="quote-title">Initial Discussion Summary</h2>
<p class="sub">Prepared for <b>${q.customer_name}</b>${d.studio_rep ? ` &middot; Studio Grape representative: <b>${d.studio_rep}</b>` : ""}</p>
<div class="section pb-avoid">
  <h3 class="section-h">Engagement Overview</h3>
  <div class="kv-grid">
    <div class="kv"><div class="l">Customer</div><div class="v">${q.customer_name || "—"}</div></div>
    <div class="kv"><div class="l">Studio Grape Representative</div><div class="v">${d.studio_rep || "—"}</div></div>
    <div class="kv"><div class="l">Product Category</div><div class="v">${d.category || "—"}</div></div>
  </div>
</div>
<div class="pb-anchor"></div>
<div class="section pb-avoid">
  <h3 class="section-h">Indicative Pricing &amp; Timelines</h3>
  <table>
    <tr><th>Item</th><th class="r">Indicative</th></tr>
    <tr><td>Sampling pricing</td><td class="r">${samplingLine}</td></tr>
    <tr><td>Base pattern making charges</td><td class="r">${onwards(d.pattern_charges)}</td></tr>
    <tr><td>Pattern grading charges</td><td class="r">${perSize(d.grading_charges)}</td></tr>
    <tr><td>Production price (per piece)</td><td class="r">${onwards(d.production_price)}</td></tr>
  </table>
</div>
<div class="pb-anchor"></div>
<div class="section pb-avoid">
  <h3 class="section-h">Planning Notes</h3>
  <div class="kv-grid">
    <div class="kv"><div class="l">Tentative sampling commencement</div><div class="v">${d.sampling_start || "—"}</div></div>
  </div>
  ${d.moq_details ? `<div class="kv" style="margin-top:8px"><div class="l">MOQs discussed</div><div class="v">${String(d.moq_details).replace(/\n/g, "<br/>")}</div></div>` : ""}
</div>
<div class="pb-anchor"></div>
${notesBlock(d.notes)}
${FULL_TERMS}`;
  return { title: "Initial Discussion Summary", body };
}

function notesBlock(notes) {
  if (!notes || !String(notes).trim()) return "";
  return `<div class="section pb-avoid"><h3 class="section-h">Additional Notes</h3><p>${String(notes).replace(/\n/g, "<br/>")}</p></div><div class="pb-anchor"></div>`;
}

function buildSampling(q) {
  const d = q.data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const materials = Array.isArray(d.materials) ? d.materials : [];
  const gstRate = Number(d.gst_rate ?? 5);
  let itemsSubtotal = 0;
  const rows = items.map((it, idx) => {
    const qty = Number(it.num_samples || 1);
    const sampPerSample = Number(it.sampling_charges || 0);
    const samp = sampPerSample * qty;
    const pb = Number(it.pattern_base || 0);
    const gradingPerSize = Number(it.pattern_grading || 0);
    const gradingSizes = Number(it.grading_sizes || 0);
    const pg = gradingPerSize * gradingSizes;
    const lt = samp + pb + pg;
    itemsSubtotal += lt;
    const img = it.image
      ? `<img class="thumb" src="${it.image}" alt=""/>`
      : `<div class="thumb-ph"></div>`;
    const gradingLabel = gradingSizes
      ? `${fmtINR(gradingPerSize)} × ${gradingSizes}`
      : fmtINR(0);
    const sampLabel = qty ? `${fmtINR(sampPerSample)} × ${qty}` : fmtINR(0);
    return `<tr>
      <td>${img}</td>
      <td class="b">${it.product_name || `Style ${idx + 1}`}</td>
      <td class="r">${qty}</td>
      <td class="r">${fmtINR(pb)}</td>
      <td class="r">${gradingLabel}</td>
      <td class="r">${sampLabel}</td>
      <td class="r b">${fmtINR(lt)}</td>
    </tr>`;
  }).join("");
  let matsSubtotal = 0;
  const matsRows = materials.map((m) => {
    const qty = Number(m.quantity || 0);
    const price = Number(m.price || 0);
    const lt = qty * price;
    matsSubtotal += lt;
    return `<tr><td class="b">${m.name || "—"}</td><td class="r">${qty}</td><td class="r">${fmtINR(price)}</td><td class="r b">${fmtINR(lt)}</td></tr>`;
  }).join("");
  const subtotal = itemsSubtotal + matsSubtotal;
  const gst = (subtotal * gstRate) / 100;
  const total = subtotal + gst;

  const body = `
<h2 class="quote-title">Sampling Quote</h2>
<p class="sub">Issued to <b>${q.customer_name}</b>${d.title ? ` <span class="sub-collection">&middot; ${d.title}</span>` : ""}</p>
<div class="section pb-avoid">
  <table>
    <colgroup>
      <col style="width:58px"/>
      <col/>
      <col style="width:60px"/>
      <col style="width:90px"/>
      <col style="width:100px"/>
      <col style="width:80px"/>
      <col style="width:90px"/>
    </colgroup>
    <tr>
      <th></th>
      <th>Product</th>
      <th class="r">Samples</th>
      <th class="r">Pattern (Base)</th>
      <th class="r">Grading (₹ × sizes)</th>
      <th class="r">Sampling (₹ × qty)</th>
      <th class="r">Line Total</th>
    </tr>
    ${rows || `<tr><td colspan="7" style="text-align:center;color:${MUTED}">No line items</td></tr>`}
  </table>
  ${materials.length ? `<div class="styles-sub"><span>Styles subtotal</span>${fmtINR(itemsSubtotal)}</div>` : ""}
</div>
<div class="pb-anchor"></div>
${materials.length ? `
<div class="section pb-avoid">
  <h3 class="section-h">Materials</h3>
  <table>
    <tr><th>Material</th><th class="r">No.</th><th class="r">Price / unit</th><th class="r">Total</th></tr>
    ${matsRows}
  </table>
  <div class="styles-sub"><span>Materials subtotal</span>${fmtINR(matsSubtotal)}</div>
</div>
<div class="pb-anchor"></div>` : ""}
<div class="subtotals-wrap pb-avoid"><div class="subtotals">
  <div class="row"><span class="l">Subtotal</span><span class="v">${fmtINR(subtotal)}</span></div>
  <div class="row"><span class="l">GST @ ${gstRate}%</span><span class="v">${fmtINR(gst)}</span></div>
  <div class="total"><div class="l">Grand Total</div><div class="v">${fmtINR(total)}</div></div>
</div></div>
${d.special_instructions ? `<div class="section pb-avoid"><h3 class="section-h">Special Instructions</h3><p>${String(d.special_instructions).replace(/\n/g, "<br/>")}</p></div>` : ""}
<div class="pb-anchor"></div>
${notesBlock(d.notes)}
${PAYMENT_BLOCK}
<div class="pb-anchor"></div>
${SAMPLING_TERMS}`;
  return { title: "Sampling Quote", body };
}

function buildProduction(q) {
  const d = q.data || {};
  const items = Array.isArray(d.items) ? d.items : [];
  const materials = Array.isArray(d.materials) ? d.materials : [];
  const gstRate = Number(d.gst_rate ?? 5);
  let stylesSubtotal = 0;
  const stylesRows = items.map((it, idx) => {
    const pb = Number(it.pattern_base || 0);
    const gradingPerSize = Number(it.pattern_grading || 0);
    const gradingSizes = Number(it.grading_sizes || 0);
    const pg = gradingPerSize * gradingSizes;
    const pieces = Number(it.num_pieces || 0);
    const pp = Number(it.production_price || 0);
    const label = (it.pricing_type === "jobwork") ? "Jobwork" : "FOB";
    const lt = pb + pg + pp * pieces;
    stylesSubtotal += lt;
    const img = it.image
      ? `<img class="thumb" src="${it.image}" alt=""/>`
      : `<div class="thumb-ph"></div>`;
    const gradingLabel = gradingSizes
      ? `${fmtINR(gradingPerSize)} × ${gradingSizes}`
      : fmtINR(0);
    return `<tr>
      <td>${img}</td>
      <td><div class="b">${it.product_name || `Style ${idx + 1}`}</div>${it.style_no ? `<div style="color:${MUTED};font-size:9.5px;margin-top:2px">${it.style_no}</div>` : ""}</td>
      <td class="r">${pieces}</td>
      <td class="r">${fmtINR(pb)}</td>
      <td class="r">${gradingLabel}</td>
      <td class="r">${fmtINR(pp)}<div style="color:${MUTED};font-size:9px;margin-top:1px">${label}</div></td>
      <td class="r b">${fmtINR(lt)}</td>
    </tr>`;
  }).join("");

  let matsSubtotal = 0;
  const matsRows = materials.map((m) => {
    const qty = Number(m.quantity || 0);
    const price = Number(m.price || 0);
    const lt = qty * price;
    matsSubtotal += lt;
    return `<tr><td class="b">${m.name || "—"}</td><td class="r">${qty}</td><td class="r">${fmtINR(price)}</td><td class="r b">${fmtINR(lt)}</td></tr>`;
  }).join("");

  const subtotal = stylesSubtotal + matsSubtotal;
  const gst = (subtotal * gstRate) / 100;
  const total = subtotal + gst;

  const body = `
<h2 class="quote-title">Production Quote</h2>
<p class="sub">Issued to <b>${q.customer_name}</b>${d.title ? ` <span class="sub-collection">&middot; ${d.title}</span>` : ""}</p>
<div class="section pb-avoid">
  <h3 class="section-h">Styles</h3>
  <table>
    <colgroup>
      <col style="width:58px"/>
      <col/>
      <col style="width:54px"/>
      <col style="width:84px"/>
      <col style="width:96px"/>
      <col style="width:100px"/>
      <col style="width:90px"/>
    </colgroup>
    <tr>
      <th></th>
      <th>Product</th>
      <th class="r">Pieces</th>
      <th class="r">Pattern (Base)</th>
      <th class="r">Grading (₹ × sizes)</th>
      <th class="r">Production / pc</th>
      <th class="r">Style Total</th>
    </tr>
    ${stylesRows || `<tr><td colspan="7" style="text-align:center;color:${MUTED}">No styles</td></tr>`}
  </table>
  <div class="styles-sub"><span>Styles subtotal</span>${fmtINR(stylesSubtotal)}</div>
</div>
<div class="pb-anchor"></div>
${materials.length ? `
<div class="section pb-avoid">
  <h3 class="section-h">Materials</h3>
  <table>
    <tr><th>Material</th><th class="r">No.</th><th class="r">Price / unit</th><th class="r">Total</th></tr>
    ${matsRows}
  </table>
  <div class="styles-sub"><span>Materials subtotal</span>${fmtINR(matsSubtotal)}</div>
</div>
<div class="pb-anchor"></div>` : ""}
<div class="subtotals-wrap pb-avoid"><div class="subtotals">
  <div class="row"><span class="l">Subtotal</span><span class="v">${fmtINR(subtotal)}</span></div>
  <div class="row"><span class="l">GST @ ${gstRate}%</span><span class="v">${fmtINR(gst)}</span></div>
  <div class="total"><div class="l">Grand Total</div><div class="v">${fmtINR(total)}</div></div>
</div></div>
<div class="pb-anchor"></div>
${notesBlock(d.notes)}
${PAYMENT_BLOCK}
<div class="pb-anchor"></div>
${PRODUCTION_TERMS}`;
  return { title: "Production Quote", body };
}

function buildQuote(q) {
  if (q.quote_type === "initial_meeting") return buildInitial(q);
  if (q.quote_type === "sampling") return buildSampling(q);
  return buildProduction(q);
}

export function renderQuoteHTML(q, opts) {
  const { title, body } = buildQuote(q);
  return shell(title, body, q.quote_number, fmtDate(q.created_at), opts);
}

/* ---------- WhatsApp message templates ---------- */
export function whatsappMessage(q) {
  const type = q.quote_type;
  const name = q.customer_name || "there";
  const docLabel = type === "initial_meeting" ? "Initial Discussion Summary"
    : type === "sampling" ? "Sampling Quote"
    : "Production Quote";
  return `Hi ${name},\n\nHere is your ${docLabel}. Kindly go through the details and feel free to reach out to us in case of any discrepancies, clarifications, or questions. We will be happy to assist.`;
}

export function quoteDocumentLabel(q) {
  if (q.quote_type === "initial_meeting") return "Initial Discussion Summary";
  if (q.quote_type === "sampling") return "Sampling Quote";
  return "Production Quote";
}

/* ---------- Preview (HTML in new tab) ---------- */
export function openQuotePDF(q) {
  const html = renderQuoteHTML(q);
  const w = window.open("", "_blank");
  if (!w) {
    alert("Please allow pop-ups to preview the quote PDF.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

/* ---------- Generate real PDF Blob via html2canvas → jsPDF (deterministic, no clipping) ---------- */
async function generateQuotePDFBlob(q) {
  const { title, body } = buildQuote(q);
  const innerHTML = bodyOnlyHTML(title, body, q.quote_number, fmtDate(q.created_at), LOGO_URL);

  const styleTag = document.createElement("style");
  styleTag.setAttribute("data-sg-pdf", "1");
  styleTag.textContent = STYLES;
  document.head.appendChild(styleTag);

  const SRC_WIDTH = 794;

  const stage = document.createElement("div");
  stage.style.cssText = `position:fixed;left:0;top:0;width:${SRC_WIDTH}px;background:#fff;z-index:999998;`;
  const doc = document.createElement("div");
  doc.className = "sg-pdf-doc";
  doc.style.width = SRC_WIDTH + "px";
  doc.innerHTML = innerHTML;
  stage.appendChild(doc);
  document.body.appendChild(stage);

  const overlay = document.createElement("div");
  overlay.style.cssText = "position:fixed;inset:0;background:rgba(255,255,255,0.98);z-index:999999;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:-apple-system,sans-serif;color:#3E1837;font-weight:700;font-size:15px;gap:14px;";
  overlay.innerHTML = `
    <div style="width:42px;height:42px;border:3px solid #E8E3E6;border-top-color:#3E1837;border-radius:50%;animation:sg-spin 0.8s linear infinite"></div>
    <div>Preparing your PDF…</div>
    <style>@keyframes sg-spin{to{transform:rotate(360deg)}}</style>`;
  document.body.appendChild(overlay);

  // Preload images
  await Promise.all(Array.from(doc.querySelectorAll("img")).map((img) => new Promise((res) => {
    if (img.complete && img.naturalWidth > 0) return res();
    img.onload = img.onerror = () => res();
    setTimeout(res, 3000);
  })));
  await new Promise((r) => setTimeout(r, 250));

  try {
    // 1) Rasterize the source at 2× pixel density for crisp text.
    const canvas = await html2canvas(doc, {
      scale: 2,
      width: SRC_WIDTH,
      windowWidth: SRC_WIDTH,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 8000,
    });

    // 2) Map the canvas onto A4 pages (210mm × 297mm). Paginate by slicing vertically.
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    const pageWidthMM = pdf.internal.pageSize.getWidth();
    const pageHeightMM = pdf.internal.pageSize.getHeight();
    const fullImgHeightMM = (canvas.height * pageWidthMM) / canvas.width;

    if (fullImgHeightMM <= pageHeightMM) {
      // Single page
      const imgData = canvas.toDataURL("image/jpeg", 0.94);
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidthMM, fullImgHeightMM, undefined, "FAST");
    } else {
      // Multi-page with smart breaks at pb-anchor / pb-avoid boundaries
      const pxPerMM = canvas.width / pageWidthMM;
      const naivePageHeightPx = Math.floor(pageHeightMM * pxPerMM);
      const renderScale = canvas.width / SRC_WIDTH; // html2canvas scale

      // Collect candidate "safe break" Y positions (in canvas px)
      const safeBreaks = new Set([0]);
      doc.querySelectorAll(".pb-anchor").forEach((el) => {
        const top = el.offsetTop * renderScale;
        if (top > 0 && top < canvas.height) safeBreaks.add(Math.floor(top));
      });
      // Also use the END of each pb-avoid block as a safe break (don't cut inside it)
      doc.querySelectorAll(".pb-avoid").forEach((el) => {
        const bottom = (el.offsetTop + el.offsetHeight) * renderScale;
        if (bottom > 0 && bottom < canvas.height) safeBreaks.add(Math.floor(bottom + 4));
      });
      safeBreaks.add(canvas.height);
      const sortedBreaks = Array.from(safeBreaks).sort((a, b) => a - b);

      // Greedy: walk through page-height chunks, snap to nearest safe break ≤ chunk end
      let yPx = 0;
      let first = true;
      while (yPx < canvas.height) {
        const targetEnd = yPx + naivePageHeightPx;
        let cutY = canvas.height;
        // Find the largest safe break that is > yPx AND ≤ targetEnd
        for (let i = sortedBreaks.length - 1; i >= 0; i--) {
          const bp = sortedBreaks[i];
          if (bp > yPx && bp <= targetEnd) {
            cutY = bp;
            break;
          }
        }
        // If no safe break found in range, just cut at targetEnd
        if (cutY === canvas.height && targetEnd < canvas.height) cutY = targetEnd;
        const sliceH = cutY - yPx;
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -yPx);
        const sliceImg = sliceCanvas.toDataURL("image/jpeg", 0.94);
        const sliceHeightMM = sliceH / pxPerMM;
        if (!first) pdf.addPage();
        pdf.addImage(sliceImg, "JPEG", 0, 0, pageWidthMM, sliceHeightMM, undefined, "FAST");
        first = false;
        yPx = cutY;
      }
    }

    return pdf.output("blob");
  } finally {
    document.body.removeChild(stage);
    document.body.removeChild(overlay);
    document.head.removeChild(styleTag);
  }
}

/* ---------- Share PDF via native share sheet ---------- */
// The Web Share API on mobile browsers has a strict "must be handling a user
// gesture" restriction. Because our PDF is generated via html2canvas (async,
// several seconds), the original click-gesture is often lost by the time we
// call navigator.share(). If that happens we return the ready-to-share blob so
// the UI can render a "Tap to share" follow-up button whose click IS a fresh
// user gesture, and pass it back into `shareBlob()` below.
export async function generateQuoteShareBundle(q, { messageOverride } = {}) {
  const blob = await generateQuotePDFBlob(q);
  const filename = `${q.quote_number} - ${quoteDocumentLabel(q)}.pdf`.replace(/[^\w\s.\-()]/g, "");
  const text = messageOverride || whatsappMessage(q);
  const title = `Studio Grape · ${q.quote_number}`;
  return { blob, filename, text, title };
}

export function shareBlob({ blob, filename, text, title }) {
  const file = new File([blob], filename, { type: "application/pdf" });
  const shareData = { files: [file], title, text };
  if (navigator.canShare && navigator.canShare(shareData)) {
    return navigator.share(shareData).then(() => ({ ok: true, shared: true }));
  }
  return Promise.resolve({ ok: true, shared: false });
}

export function downloadBlob({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function shareQuotePDF(q, { messageOverride } = {}) {
  try {
    const bundle = await generateQuoteShareBundle(q, { messageOverride });
    try {
      const res = await shareBlob(bundle);
      if (res.shared) return { ok: true, shared: true };
      // canShare returned false — treat like a download.
      downloadBlob(bundle);
      return { ok: true, shared: false, bundle };
    } catch (err) {
      if (err?.name === "AbortError") return { ok: true, shared: false, cancelled: true };
      // Gesture-lost path — return the bundle so the UI can offer a "Tap to
      // share" follow-up button.
      const msg = String(err?.message || "").toLowerCase();
      if (msg.includes("user gesture") || err?.name === "NotAllowedError") {
        return { ok: true, shared: false, gestureLost: true, bundle };
      }
      throw err;
    }
  } catch (err) {
    console.error("[sharePDF] failed:", err);
    throw err;
  }
}

/* ---------- Download only ---------- */
export async function downloadQuotePDF(q) {
  const blob = await generateQuotePDFBlob(q);
  const filename = `${q.quote_number} - ${quoteDocumentLabel(q)}.pdf`.replace(/[^\w\s.\-()]/g, "");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
